import Decimal from 'decimal.js';
import { stringify } from 'query-string';
import { CurrencyEnum, NetworkEnum, currencyUtils } from '@spectrocoin/sc-currencies';
import CurrencyType from '../../enums/CurrencyType';
import { DeviceNameType, getDevice } from '../deviceHelper';

export const includedCurrencies = ['GBP', 'EUR', 'USD'];

Decimal.set({ rounding: Decimal.ROUND_DOWN });

export const toDecimal = (value: number | string) => {
	return new Decimal(value);
};

export const getCurrencyProtocol = (currencyCode: CurrencyEnum) => {
	switch (currencyCode) {
		case CurrencyEnum.BTC:
			return 'bitcoin';
		case CurrencyEnum.LTC:
			return 'litecoin';
		case CurrencyEnum.XRP:
			return 'ripple';
		case CurrencyEnum.XLM:
			return 'stellar';
		case CurrencyEnum.ALGO:
			return 'algorand';
		default:
			return currencyCode.toLowerCase();
	}
};

export const swapSeparators = (value: string) =>
	value
		.split('')
		.map((char) => (char === ',' ? '.' : char === '.' ? ',' : char))
		.join('');

export const formatAmount = (amount: string) => {
	const isAppleOS = getDevice() === DeviceNameType.IPHONE;
	return isAppleOS ? swapSeparators(amount) : amount;
};

export const formatPrecision = (
	amount: string | number,
	currencyCode: CurrencyEnum,
	isSeparatorOn = true
) => {
	const number = toDecimal(amount)
		.abs()
		.toFixed(currencyUtils.tryGetConfig(currencyCode)?.precision)
		.toString();

	if (isSeparatorOn) {
		return formatAmount(
			`${number.split('.')[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}${
				number.split('.')[1] ? `.${number.split('.')[1]}` : ``
			}`
		);
	}
	return formatAmount(number);
};

const getMessage = (currCode: CurrencyEnum, message: string | null) => {
	switch (currCode) {
		case CurrencyEnum.XLM:
		case CurrencyEnum.XEM:
			return { memo: message };
		case CurrencyEnum.XRP:
			return { dt: message };
		default:
			return {};
	}
};

const getAmount = (amount: string | null) => {
	return amount ? { amount } : {};
};

export const getQRValue = (
	currCode: CurrencyEnum,
	cAddress: string,
	msg: string | null,
	amount: string | null
) => {
	const params = stringify({ ...getAmount(amount), ...getMessage(currCode, msg) });
	return `${getCurrencyProtocol(currCode)}:${cAddress}${params ? `?${params}` : ''}`;
};

export const isErcCurrency = (currency: CurrencyEnum) =>
	currencyUtils.getConfigOrDefault(currency).isErc;

export const getCurrencyType = (currCode: CurrencyEnum): CurrencyType => {
	const currency = currencyUtils.tryGetConfig(currCode);
	if (!currency) return CurrencyType.None;
	return currency.isVirtual ? CurrencyType.Virtual : CurrencyType.Fiat;
};

const currencyTypeSortPriority = {
	[CurrencyType.Virtual]: 3,
	[CurrencyType.Fiat]: 2,
	[CurrencyType.None]: 1,
} as const;

const compareCurrencies = (
	priorityMap: Record<CurrencyEnum, number>,
	currencyA: CurrencyEnum,
	currencyB: CurrencyEnum
) => {
	return (
		(priorityMap[currencyB] || 0) - (priorityMap[currencyA] || 0) ||
		currencyTypeSortPriority[getCurrencyType(currencyB)] -
			currencyTypeSortPriority[getCurrencyType(currencyA)] ||
		currencyA.localeCompare(currencyB, undefined, {
			numeric: true,
			sensitivity: 'base',
		})
	);
};

const receiveCurrencyCodeSortPriority = {
	[CurrencyEnum.BTC]: 8,
	[CurrencyEnum.ETH]: 7,
	[CurrencyEnum.USDT]: 6,
	[CurrencyEnum.USDC]: 5,
	[CurrencyEnum.EUR]: 4,
	[CurrencyEnum.USD]: 3,
	[CurrencyEnum.GBP]: 2,
	[CurrencyEnum.XRP]: 1,
} as Record<CurrencyEnum, number>;

export const compareReceiveCurrencies = (currencyA: CurrencyEnum, currencyB: CurrencyEnum) => {
	return compareCurrencies(receiveCurrencyCodeSortPriority, currencyA, currencyB);
};

const payCurrencyCodeSortPriority = {
	[CurrencyEnum.BTC]: 4,
	[CurrencyEnum.ETH]: 3,
	[CurrencyEnum.USDT]: 2,
	[CurrencyEnum.USDC]: 1,
} as Record<CurrencyEnum, number>;

export const comparePayCurrencies = (currencyA: CurrencyEnum, currencyB: CurrencyEnum) => {
	return compareCurrencies(payCurrencyCodeSortPriority, currencyA, currencyB);
};

const networkSortPriority = {
	[NetworkEnum.ETH]: 3,
	[NetworkEnum.BSC]: 2,
	[NetworkEnum.TRX]: 1,
} as Record<NetworkEnum, number>;

export const compareNetworks = (networkA: NetworkEnum, networkB: NetworkEnum) => {
	return (
		(networkSortPriority[networkB] || 0) - (networkSortPriority[networkA] || 0) ||
		networkA.localeCompare(networkB, undefined, {
			numeric: true,
			sensitivity: 'base',
		})
	);
};
